import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { api, API_BASE } from '../../api/client';

interface FaqItem {
  id: number;
  question: string;
  answer: string;
  category: string;
  language: string;
  sortOrder: number;
}

const EMPTY = { question: '', answer: '', category: '', language: 'ru', sortOrder: '0' };

export default function AdminFaqPage() {
  const [items, setItems] = useState<FaqItem[]>([]);
  const [form, setForm] = useState(EMPTY);
  const [editId, setEditId] = useState<number | null>(null);
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState('');

  const load = () => axios.get(`${API_BASE}/faq`).then(r => setItems(r.data));
  useEffect(() => { load(); }, []);

  const startEdit = (f: FaqItem) => {
    setEditId(f.id);
    setForm({
      question: f.question,
      answer: f.answer,
      category: f.category || '',
      language: f.language || 'ru',
      sortOrder: String(f.sortOrder ?? 0),
    });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const cancel = () => { setEditId(null); setForm(EMPTY); };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    try {
      const body = { ...form, sortOrder: Number(form.sortOrder) };
      if (editId) {
        await api.put(`/faq/${editId}`, body);
      } else {
        await api.post('/faq', body);
      }
      setForm(EMPTY);
      setEditId(null);
      await load();
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id: number) => {
    if (!window.confirm('Удалить вопрос?')) return;
    await api.delete(`/faq/${id}`);
    await load();
  };

  const filtered = items.filter(f =>
    !search || f.question.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div style={styles.page}>
      <h2 style={styles.title}>Управление FAQ</h2>

      {/* Форма */}
      <div style={styles.card}>
        <h3 style={styles.cardTitle}>{editId ? '✏️ Редактировать вопрос' : '➕ Добавить вопрос'}</h3>
        <form onSubmit={handleSubmit} style={styles.form}>

          <div style={styles.field}>
            <label style={styles.label}>Язык *</label>
            <select style={styles.input} value={form.language}
              onChange={e => setForm({ ...form, language: e.target.value })}>
              <option value="ru">🇷🇺 Русский</option>
              <option value="ky">🇰🇬 Кыргызский</option>
              <option value="en">🇬🇧 English</option>
            </select>
          </div>

          <div style={styles.field}>
            <label style={styles.label}>Вопрос *</label>
            <textarea style={{ ...styles.input, resize: 'vertical' }} rows={2} value={form.question}
              onChange={e => setForm({ ...form, question: e.target.value })}
              placeholder="Как вступить в профсоюз?" required />
          </div>

          <div style={styles.field}>
            <label style={styles.label}>Ответ *</label>
            <textarea style={{ ...styles.input, resize: 'vertical' }} rows={4} value={form.answer}
              onChange={e => setForm({ ...form, answer: e.target.value })}
              placeholder="Подайте заявление..." required />
          </div>

          <div style={styles.field}>
            <label style={styles.label}>Категория</label>
            <input style={styles.input} value={form.category}
              onChange={e => setForm({ ...form, category: e.target.value })}
              placeholder="membership, rights, documents..." />
          </div>

          <div style={styles.field}>
            <label style={styles.label}>Порядок сортировки</label>
            <input style={styles.input} type="number" value={form.sortOrder}
              onChange={e => setForm({ ...form, sortOrder: e.target.value })} />
          </div>

          <div style={{ display: 'flex', gap: 10 }}>
            <button style={styles.btn} type="submit" disabled={saving}>
              {saving ? 'Сохранение...' : editId ? '💾 Сохранить' : '➕ Добавить'}
            </button>
            {editId && <button type="button" onClick={cancel} style={styles.btnCancel}>Отмена</button>}
          </div>
        </form>
      </div>

      {/* Список */}
      <div style={styles.card}>
        <div style={styles.listHeader}>
          <h3 style={styles.listTitle}>Все вопросы ({items.length})</h3>
          <input style={styles.searchInput} placeholder="🔍 Поиск..."
            value={search} onChange={e => setSearch(e.target.value)} />
        </div>

        {filtered.length === 0 ? (
          <p style={styles.empty}>Вопросов пока нет</p>
        ) : (
          filtered.map(f => (
            <div key={f.id} style={styles.faqRow}>
              <div style={styles.faqContent}>
                <div style={styles.faqMeta}>
                  <span style={styles.langBadge}>{f.language === 'ru' ? '🇷🇺' : f.language === 'ky' ? '🇰🇬' : '🇬🇧'}</span>
                  {f.category && <span style={styles.badge}>{f.category}</span>}
                  <span style={styles.sortNum}>#{f.sortOrder}</span>
                </div>
                <div style={styles.faqQ}>{f.question}</div>
                <div style={styles.faqA}>{f.answer.slice(0, 150)}{f.answer.length > 150 ? '...' : ''}</div>
              </div>
              <div style={styles.faqActions}>
                <button onClick={() => startEdit(f)} style={styles.btnEdit}>✏️</button>
                <button onClick={() => remove(f.id)} style={styles.btnDanger}>🗑</button>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  page: { padding: 'clamp(16px, 4vw, 32px)', maxWidth: 1000, margin: '0 auto' },
  title: { color: '#1a3a6b', fontSize: 'clamp(1.3rem, 3vw, 1.6rem)', marginBottom: 'clamp(16px, 3vw, 24px)' },
  card: { background: '#fff', borderRadius: 10, boxShadow: '0 2px 12px rgba(0,0,0,0.08)', marginBottom: 16, overflow: 'hidden' },
  cardTitle: { color: '#1a3a6b', fontSize: 'clamp(0.9rem, 2vw, 1rem)', padding: '16px 20px', borderBottom: '1px solid #f0f4fb' },
  form: { display: 'flex', flexDirection: 'column', gap: 12, padding: '16px 20px 20px' },
  row: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 280px), 1fr))', gap: 12 },
  field: { display: 'flex', flexDirection: 'column', gap: 4 },
  label: { fontSize: '0.85rem', fontWeight: 600, color: '#444' },
  input: { padding: '9px 12px', borderRadius: 8, border: '1px solid #dde3ef', fontSize: '0.9rem', outline: 'none', width: '100%' },
  btn: { padding: '9px 20px', background: '#1a3a6b', color: '#fff', border: 'none', borderRadius: 8, fontWeight: 600, cursor: 'pointer' },
  btnCancel: { padding: '9px 20px', background: '#f0f4fb', color: '#555', border: 'none', borderRadius: 8, cursor: 'pointer' },
  empty: { padding: '16px 20px', color: '#888', fontSize: '0.9rem' },
  listHeader: { padding: '16px 20px', borderBottom: '1px solid #f0f4fb', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap' },
  listTitle: { color: '#1a3a6b', margin: 0, fontSize: 'clamp(0.9rem, 2vw, 1rem)' },
  searchInput: { padding: '9px 12px', borderRadius: 8, border: '1px solid #dde3ef', fontSize: '0.9rem', outline: 'none', width: 'clamp(180px, 40vw, 240px)', margin: 0 },
  faqRow: { display: 'flex', gap: 12, padding: '14px 20px', borderTop: '1px solid #f0f4fb', alignItems: 'flex-start', flexWrap: 'wrap' },
  faqContent: { flex: 1, minWidth: 200 },
  faqMeta: { display: 'flex', gap: 8, marginBottom: 6, alignItems: 'center', flexWrap: 'wrap' },
  langBadge: { fontSize: '0.85rem', fontWeight: 600 },
  badge: { background: '#e8f0fe', color: '#1a3a6b', fontSize: '0.75rem', padding: '2px 8px', borderRadius: 10, fontWeight: 600 },
  sortNum: { fontSize: '0.75rem', color: '#aaa' },
  faqQ: { fontSize: '0.9rem', fontWeight: 600, color: '#1a2a4a', marginBottom: 2, wordBreak: 'break-word' },
  faqA: { fontSize: '0.82rem', color: '#888', marginTop: 4, wordBreak: 'break-word' },
  faqActions: { display: 'flex', gap: 6, flexShrink: 0 },
  btnEdit: { padding: '6px 10px', background: '#f39c12', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: '0.9rem' },
  btnDanger: { padding: '6px 10px', background: '#e74c3c', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: '0.9rem' },
};
